import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ConversationSummaryCard } from '../../components/speedToLead/ConversationSummaryCard';
import { summaryViewOf } from '../../lib/leadBot/smsClassification';
import { SelectField } from '../../components/ui/SelectField';
import { type BotConversation } from '../../types/leadBot';
import { LEAD_OUTCOMES, LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import { setConversationOutcome } from './api';

// ⚠️⚠️ THE FIELD THE WHOLE REPORT RESTS ON, and the reason it exists on the
// transcript viewer rather than somewhere tidier.
//
// Booked calls and appointments are the numbers that decide whether this bot is
// commercially worth anything, and they are NOT DERIVABLE from anything we
// store. So somebody has to say. This is where they already are — reading what
// actually happened — and every extra click between here and recording it is a
// conversation that ends up unclassified.
//
// ⚠️ AND IT CANNOT BE BACKFILLED. A conversation that closes without an outcome
// has lost its result permanently: the transcript survives, but nobody goes back
// through months of them to reconstruct what came of each one.
//
// ⚠️ IT IS ALWAYS EDITABLE, in any state. An outcome usually becomes known AFTER
// the conversation ends — the lead turned up, or didn't — so a control that
// locked on close would be a control that is wrong whenever reality arrives
// late. Re-classifying is a normal correction, and it is audited.

export function OutcomePicker({
  conversation,
  onSaved,
}: {
  conversation: BotConversation;
  onSaved: () => void;
}) {
  const save = useMutation({
    mutationFn: (outcome: LeadOutcome) =>
      setConversationOutcome(conversation.id, outcome),
    onSuccess: () => {
      toast.success('Outcome recorded.');
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const unclassified = conversation.outcome === 'unresolved';
  const summary = summaryViewOf(conversation.classification);

  // An AI summary exists: show it with the SHARED outcome picker, the suggestion pre-selected and saved only on Confirm.
  if (summary) {
    return (
      <ConversationSummaryCard
        id="conversation-outcome"
        summary={summary}
        current={conversation.outcome}
        setBy={conversation.outcomeSetBy ? 'person' : null}
        onSave={async (code) => {
          await setConversationOutcome(conversation.id, code);
          toast.success('Outcome recorded.');
          onSaved();
        }}
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="text-xs font-medium text-muted-foreground" htmlFor="conversation-outcome">
        What came of it?
      </label>
      <SelectField
        id="conversation-outcome"
        size="sm"
        value={conversation.outcome}
        onValueChange={(v) => save.mutate(v as LeadOutcome)}
        disabled={save.isPending}
        options={LEAD_OUTCOMES.map((outcome) => ({
          value: outcome,
          label: LEAD_OUTCOME_LABELS[outcome],
        }))}
      />
      {/* ⚠️ SAYS WHY IT MATTERS, because the cost of skipping it is invisible at
          the moment of skipping it — the number is simply gone later. */}
      {unclassified ? (
        <span className="text-xs text-amber-700">
          Not recorded yet — this can't be worked out later, so it's worth a moment now.
        </span>
      ) : (
        conversation.outcomeSetByName && (
          <span className="text-xs text-muted-foreground">
            Recorded by {conversation.outcomeSetByName}
          </span>
        )
      )}
    </div>
  );
}
